// 🚪 JoinRoom.js - FIX VERIFICA SALA PERSISTENTE
// ✅ Risolve: "Verifica sala in corso..." infinito
// ✅ Usa endpoint pubblico /api/rooms/verify/:roomId

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// ✅ Normalizza URL API
function getApiUrl() {
  let url = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';
  url = url.replace(/\/$/, '');
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `https://${url}`;
  }
  console.log('🔧 API_URL:', url);
  return url;
}

const API_URL = getApiUrl();

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const JoinCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 107, 53, 0.3);
  border-radius: 24px;
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  font-size: 3rem;
`;

const Title = styled.h1`
  color: white;
  font-size: 2rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const RoomName = styled.div`
  color: #FF6B35;
  font-size: 1.3rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(255, 107, 53, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(255, 107, 53, 0.3);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  font-size: 0.95rem;
`;

const Input = styled.input`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #FF6B35;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  font-weight: 600;
  
  input {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }
`;

const JoinButton = styled(motion.button)`
  padding: 1.2rem;
  background: linear-gradient(135deg, #FF6B35, #F7931E);
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.2);
  border: 2px solid rgba(239, 68, 68, 0.5);
  color: #ef4444;
  padding: 1rem;
  border-radius: 12px;
  text-align: center;
  font-weight: 600;
`;

const LoadingMessage = styled.div`
  background: rgba(59, 130, 246, 0.2);
  border: 2px solid rgba(59, 130, 246, 0.5);
  color: #3b82f6;
  padding: 1rem;
  border-radius: 12px;
  text-align: center;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 3px solid rgba(59, 130, 246, 0.3);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const BackButton = styled.button`
  margin-top: 1rem;
  padding: 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const RoomInfo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  
  div {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

function JoinRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  
  const [displayName, setDisplayName] = useState('');
  const [roomPassword, setRoomPassword] = useState('');
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roomInfo, setRoomInfo] = useState(null);

  useEffect(() => {
    // Carica display name salvato
    const savedName = localStorage.getItem('displayName');
    if (savedName) {
      setDisplayName(savedName);
    }
    
    // Verifica sala
    verifyRoom();
  }, [roomId]);

  const verifyRoom = async () => {
    console.log('🔍 Verifica sala:', roomId);
    console.log('🔗 URL verifica:', `${API_URL}/api/rooms/verify/${roomId}`);
    
    try {
      // ✅ Chiama endpoint pubblico /api/rooms/verify/:roomId
      const response = await fetch(`${API_URL}/api/rooms/verify/${roomId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Risposta verifica:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Sala trovata:', data);
        
        setRoomInfo({
          name: data.room.name,
          requiresPassword: data.room.requirePassword,
          maxParticipants: data.room.max_participants,
          activeParticipants: data.room.activeParticipants || 0
        });
        setLoading(false);
      } else {
        const data = await response.json();
        console.error('❌ Sala non trovata:', data);
        setError(data.message || 'Sala non trovata o non più attiva');
        setLoading(false);
      }
    } catch (err) {
      console.error('❌ Errore verifica sala:', err);
      setError('Errore di connessione al server. Verifica che il backend sia avviato.');
      setLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    
    if (!displayName.trim()) {
      setError('Inserisci il tuo nome');
      return;
    }
    
    if (roomInfo?.requiresPassword && !roomPassword.trim()) {
      setError('Questa sala richiede una password');
      return;
    }

    // Salva display name
    localStorage.setItem('displayName', displayName);

    // Entra nella stanza
    navigate(`/room/${roomId}`, {
      state: {
        displayName,
        videoEnabled,
        audioEnabled,
        roomPassword: roomInfo?.requiresPassword ? roomPassword : null
      }
    });
  };

  const handleBack = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <PageContainer>
        <JoinCard
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Logo>🔍</Logo>
          <Title>Verifica sala in corso...</Title>
          <LoadingMessage>
            <Spinner />
            <span>Controllo disponibilità sala</span>
          </LoadingMessage>
        </JoinCard>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <JoinCard
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Logo>❌</Logo>
          <Title>Errore</Title>
          <ErrorMessage>{error}</ErrorMessage>
          <BackButton onClick={handleBack}>
            🏠 Torna alla Home
          </BackButton>
        </JoinCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <JoinCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Logo>🚪</Logo>
        <Title>Entra nella Sala</Title>
        
        {roomInfo && (
          <>
            <RoomName>📺 {roomInfo.name}</RoomName>
            <RoomInfo>
              <div>👥 Partecipanti: {roomInfo.activeParticipants}/{roomInfo.maxParticipants}</div>
              {roomInfo.requiresPassword && (
                <div>🔒 Sala protetta da password</div>
              )}
            </RoomInfo>
          </>
        )}

        <Form onSubmit={handleJoin}>
          <InputGroup>
            <Label>👤 Nome</Label>
            <Input
              type="text"
              placeholder="Inserisci il tuo nome"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </InputGroup>

          {roomInfo?.requiresPassword && (
            <InputGroup>
              <Label>🔐 Password Sala</Label>
              <Input
                type="password"
                placeholder="Inserisci password"
                value={roomPassword}
                onChange={(e) => setRoomPassword(e.target.value)}
                required
              />
            </InputGroup>
          )}

          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={videoEnabled}
                onChange={(e) => setVideoEnabled(e.target.checked)}
              />
              🎥 Video
            </CheckboxLabel>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={audioEnabled}
                onChange={(e) => setAudioEnabled(e.target.checked)}
              />
              🎤 Audio
            </CheckboxLabel>
          </CheckboxGroup>

          <JoinButton
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            🚀 Entra nella Sala
          </JoinButton>
        </Form>

        <BackButton onClick={handleBack}>
          🏠 Torna alla Home
        </BackButton>
      </JoinCard>
    </PageContainer>
  );
}

export default JoinRoom;
